import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ListingRow } from '@marstein-radar/shared';
import { useAuth } from '../auth/AuthContext';
import { buttonStyle, formatEur } from '../styles';

// Fáza 2 inzertnej sekcie (2026-07-15) — bare-bones moderačná fronta, gated server-side
// cez AdminGuard (ADMIN_EMAILS). Žiadna client-side kontrola admin oprávnenia — ak
// user nie je admin, /admin/listings/pending vráti 403 a stránka to len zobrazí ako
// chybu (nie je to bezpečnostná hranica, tá je na backende). Zámerne bez štýlingovej
// investície, viď plán.
export function AdminListingsPage() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [forbidden, setForbidden] = useState(false);

  const { data: pending } = useQuery<ListingRow[]>({
    queryKey: ['listings-admin-pending'],
    queryFn: async () => {
      const res = await fetch('/api/v1/admin/listings/pending');
      if (res.status === 403) {
        setForbidden(true);
        return [];
      }
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!user,
  });

  if (loading) return null;
  if (!user) return <Navigate to="/prihlasenie" replace />;

  async function approve(id: string): Promise<void> {
    await fetch(`/api/v1/admin/listings/${id}/approve`, { method: 'POST' });
    await qc.invalidateQueries({ queryKey: ['listings-admin-pending'] });
  }

  async function reject(id: string): Promise<void> {
    await fetch(`/api/v1/admin/listings/${id}/reject`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    setRejectingId(null);
    setReason('');
    await qc.invalidateQueries({ queryKey: ['listings-admin-pending'] });
  }

  if (forbidden) {
    return (
      <main className="page">
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          Nemáš admin oprávnenie.
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="page-header">
        <h1>Moderácia inzerátov</h1>
      </div>

      {pending && pending.length === 0 && (
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          Žiadne inzeráty na schválenie.
        </div>
      )}

      {pending?.map((l) => (
        <div key={l.id} className="fin-panel" style={{ padding: '16px 20px', marginBottom: 12 }}>
          <strong>{l.title}</strong> —{' '}
          {l.priceCents != null ? formatEur(l.priceCents / 100) : 'dohodou'}
          <p style={{ whiteSpace: 'pre-wrap' }}>{l.description}</p>
          <div className="listing-card-meta">
            {l.contactEmail}
            {l.isInsolvencyRelated ? ' · súvisí s insolvenciou' : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={() => approve(l.id)} style={buttonStyle}>
              Schváliť
            </button>
            <button type="button" onClick={() => setRejectingId(l.id)} style={buttonStyle}>
              Zamietnuť
            </button>
          </div>
          {rejectingId === l.id && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Dôvod zamietnutia"
                style={{ flex: 1 }}
              />
              <button type="button" onClick={() => reject(l.id)} style={buttonStyle}>
                Potvrdiť zamietnutie
              </button>
            </div>
          )}
        </div>
      ))}
    </main>
  );
}
